/**
 * HTTP client for the server-authoritative backend (apps/backend, Phase 3).
 *
 * The engine no longer runs client-side: every read and every action is a
 * round-trip to `/game/*`, authenticated with the Supabase bearer token from
 * `supabase-auth.ts`. `GameProvider` is the only consumer.
 */

import type { EngineEvent, GameState } from '@spacegame/engine';

export interface GameResponse {
  state: GameState;
  events: EngineEvent[];
}

export type MessageCategory = 'combat' | 'espionage' | 'private';
export interface MessageRecord {
  id: number;
  category: MessageCategory;
  subject: string;
  senderName: string | null;
  body: string | null;
  data: Record<string, unknown> | null;
  read: boolean;
  createdAt: string;
}

/** The backend rejected the request (bad action, failed validation, `ActionError`) — HTTP 4xx. */
export class ApiActionError extends Error {}

/** The backend could not be reached or failed unexpectedly — network error or HTTP 5xx. */
export class ApiUnreachableError extends Error {}

export interface BackendClientLike {
  getState(): Promise<GameResponse>;
  dispatchAction(type: string, payload?: unknown): Promise<GameResponse>;
  resetGame(): Promise<GameResponse>;
  /** Founds a new colony (galaxy plan Milestone E) — its own endpoint since
   * it needs a backend-claimed galaxy slot, unlike the generic action table. */
  colonize(): Promise<GameResponse>;
  colonizeAt(system: number, position: number): Promise<GameResponse>;
  abandonColony(planetIndex: number): Promise<GameResponse>;
  listMessages(category?: MessageCategory, cursor?: string, limit?: number): Promise<MessageRecord[]>;
  unreadMessageCount(): Promise<number>;
  markMessageRead(id: number): Promise<void>;
  sendPrivateMessage(recipientName: string, subject: string, body: string): Promise<void>;
}

/**
 * PUBLIC by design, same reasoning as supabase-config.ts: this is the URL
 * the browser is meant to call, not a secret. Override per environment via
 * `VITE_API_URL`.
 */
const DEFAULT_API_URL = 'https://api.dysonwarfare.com';

/** Shared with activity-client.ts — one source of truth for the API origin. */
export function getApiUrl(): string {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  return env.VITE_API_URL ?? DEFAULT_API_URL;
}

export interface AccessTokenSource {
  getAccessToken(): Promise<string>;
}

export class BackendClient implements BackendClientLike {
  private readonly baseUrl: string;

  constructor(
    private readonly auth: AccessTokenSource,
    baseUrl: string = getApiUrl(),
  ) {
    this.baseUrl = baseUrl;
  }

  private async request<T = GameResponse>(path: string, init: RequestInit = {}): Promise<T> {
    let token: string;
    try {
      token = await this.auth.getAccessToken();
    } catch (e) {
      throw new ApiUnreachableError(e instanceof Error ? e.message : 'Anmeldung fehlgeschlagen.');
    }

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...init.headers },
      });
    } catch {
      throw new ApiUnreachableError('Server nicht erreichbar.');
    }

    if (response.status >= 500) {
      throw new ApiUnreachableError(`Server-Fehler (${response.status}).`);
    }
    if (response.status === 401) {
      throw new ApiUnreachableError('Sitzung ungültig.');
    }
    if (response.status >= 400) {
      const body = await response.json().catch(() => null);
      const message = body && typeof body.message === 'string' ? body.message : 'Aktion abgelehnt.';
      throw new ApiActionError(message);
    }
    return response.json() as Promise<T>;
  }

  getState(): Promise<GameResponse> {
    return this.request('/game/state');
  }

  dispatchAction(type: string, payload?: unknown): Promise<GameResponse> {
    return this.request('/game/action', { method: 'POST', body: JSON.stringify({ type, payload }) });
  }

  resetGame(): Promise<GameResponse> {
    return this.request('/game/reset', { method: 'POST' });
  }

  colonize(): Promise<GameResponse> {
    return this.request('/game/colonize', { method: 'POST' });
  }

  colonizeAt(system: number, position: number): Promise<GameResponse> {
    return this.request(`/game/colonize/2/${system}/${position}`, { method: 'POST' });
  }

  abandonColony(planetIndex: number): Promise<GameResponse> {
    return this.request(`/game/abandon-colony/${planetIndex}`, { method: 'POST' });
  }

  listMessages(category?: MessageCategory, cursor?: string, limit?: number): Promise<MessageRecord[]> {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (cursor) params.set('cursor', cursor);
    if (limit !== undefined) params.set('limit', String(limit));
    const query = params.toString();
    return this.request<MessageRecord[]>(`/messages${query ? `?${query}` : ''}`);
  }

  async unreadMessageCount(): Promise<number> {
    const result = await this.request<{ count: number }>('/messages/unread-count');
    return result.count;
  }

  async markMessageRead(id: number): Promise<void> {
    await this.request<{ ok: boolean }>(`/messages/${id}/read`, { method: 'PATCH' });
  }

  async sendPrivateMessage(recipientName: string, subject: string, body: string): Promise<void> {
    await this.request<{ ok: boolean }>('/messages', {
      method: 'POST',
      body: JSON.stringify({ recipientName, subject, body }),
    });
  }
}
